import React, { Component } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faCheckCircle,
  faExclamationTriangle,
  faExternalLinkAlt,
  faInfoCircle,
  faChevronDown,
} from '@fortawesome/free-solid-svg-icons';
import { PrismBanner } from '@prism-ui/react';
import PrismDataGrid from '../../shared/PrismDataGrid';
import PageHeader from '../../PageHeader';
import Card from '../../Card';
import Loader from '../../Loader';
import Unauthorized from '../../Unauthorized';
import './SubmittedApp.css';
import {
  COMPETENCIES_MENTEE_INFO,
  COMPETENCIES_MENTOR_INFO,
  COMPETENCIES_COMMITTEE_MEMBER_INFO,
  COMPETENCIES_ADDITIONAL_SKILLS_PROMPT,
  ESSAY_PROMPT,
  APPLICATION_STATUS_ID_PENDING,
  RATING_LABELS,
  RATING_COLORS,
} from '../constants';
import { formatMonthYear } from '../../../util/dateUtils';
import parse from 'html-react-parser';
import DOMPurify from 'dompurify';


class SubmittedApp extends Component {
  state = {
    loading: true,
    loadError: '',
    requestedApplicationId: null,
    submission: {},
    requiredTenureMonths: 24, // Default to 24 months
    fields: {
      EducationEntries: [
        {
          key: 'description',
          field: 'Institution',
        },
        {
          key: 'attainment_year',
          field: 'Year',
          width: '80px',
        },
      ],
      CareerEntries: [
        {
          key: 'company',
          field: 'Company',
        },
        {
          key: 'position',
          field: 'Position',
        },
        {
          key: 'start_date',
          field: 'Start Date',
          formatter: (value) => formatMonthYear(value),
        },
        {
          key: 'end_date',
          field: 'End Date',
          formatter: (value) => value ? formatMonthYear(value) : 'Present',
        },
      ],
      CompetencyRatings: [
        {
          key: 'Competency.name',
          field: 'Skill',
        },
        {
          key: 'rating',
          field: 'Rating',
          width: '140px',
          formatter: (value) => {
            const idx = value - 1;
            const label = RATING_LABELS[idx] || 'N/A';
            // RATING_COLORS is a categorical data-visualisation palette; the
            // fallback swatch and its white label are intentionally fixed.
            const color = RATING_COLORS[idx] || '#666';
            return (
              <span style={{
                backgroundColor: color,
                color: '#fff',
                padding: '0.2rem 0.6rem',
                borderRadius: '999px',
                fontSize: '0.8rem',
                fontWeight: 600,
                display: 'inline-block',
              }}>{label}</span>
            );
          },
        },
        // {
        //   key: "examples",
        //   field: "Examples",
        // },
      ],
    },
    validPermissionLevel: 80,
    hideHeader: false,
    approvalHistory: [],
    approvalHistoryOpen: false,
  };

  getApplication = (id) => {
    this.setState({
      loading: true,
      loadError: '',
      requestedApplicationId: String(id),
    });

    return axios.get('/api/applications/' + id)
      .then((response) => {
        this.setState(
          {
            submission: response.data,
            loading: false,
          },
          () => {
            // Fetch tenure requirement for this application's chapter and role
            this.fetchTenureRequirement();
          }
        );
      })
      .catch((error) => {
        const status = error.response?.status;
        const loadError = status === 404
          ? 'This application is no longer available. It may have been removed under the data retention policy.'
          : status === 403
            ? "You don't have access to this application."
            : 'Unable to load this application. Please try again later.';
        this.setState({ loading: false, loadError });
      });
  };

  fetchTenureRequirement = () => {
    const { chapter_id, applicant_type_id } = this.state.submission;
    
    if (chapter_id && applicant_type_id) {
      axios
        .get(`/api/role_tenure/chapter/${chapter_id}/role/${applicant_type_id}`)
        .then((response) => {
          if (response.data && response.data.tenure_months !== undefined) {
            this.setState({ requiredTenureMonths: response.data.tenure_months });
          }
        })
        .catch((err) => {
          console.warn('Could not fetch tenure requirement, using default:', err);
          // Keep default value of 24 months
        });
    }
  };

  componentDidMount = () => {
    this.getApplication(this.props.params.application_id);
  };

  componentDidUpdate = () => {
    const requestedId = this.state.requestedApplicationId;
    const newId = String(this.props.params.application_id);

    if (newId !== requestedId) {
      this.getApplication(this.props.params.application_id);
    }
  };

  /** Application Checks */
  isTenuredEnough = () => {
    let hire_date = this.state.submission.hire_date;
    const requiredYears = this.state.requiredTenureMonths / 12;

    if (hire_date === null) {
      // If hire date is null, use years_with_company
      let tenure = this.state.submission.years_with_company;
      return tenure >= requiredYears;
    } else {
      // If hire date is not null, calculate the tenure based on hire_date
      hire_date = new Date(hire_date);
      let current_date = new Date();
      let duration = current_date - hire_date;
      let threshold = 1000 * 60 * 60 * 24 * 365 * requiredYears;

      return duration > threshold;
    }
  };

  isManagerApproved = () => {
    return this.state.submission?.ManagerApproval?.approved ?? false;
  };

  hasMultipleApplications = () => {
    return this.state.submission.Applications?.length > 0;
  };

  getCompInfo = () => {
    switch (this.state.submission.applicant_type_id) {
      case 1:
        return COMPETENCIES_MENTEE_INFO;
      case 2:
        return COMPETENCIES_MENTOR_INFO;
      case 3:
        return COMPETENCIES_COMMITTEE_MEMBER_INFO;
      default:
        return '';
    }
  };

  getCompAdditionalSkillsInfo = () => {
    return COMPETENCIES_ADDITIONAL_SKILLS_PROMPT;
  };

  getEssayInfo = () => {
    return ESSAY_PROMPT;
  };

  // Resolves a dotted field path (e.g. `Chapter.location`) against an entry.
  readPath = (entry, path) =>
    path.split('.').reduce((obj, part) => (obj == null ? obj : obj[part]), entry);

  buildTable = (dataType) => {
    const fields = this.state.fields[dataType];
    const entries = this.state.submission?.[dataType] ?? [];

    // Career entries display most-recent-first. The row id is taken from the entry's
    // position in the unsorted list so it survives the reorder.
    const rows = entries
      .map((entry, index) => ({ ...entry, _rowId: `${dataType}-${index}` }))
      .sort((a, b) =>
        dataType === 'CareerEntries'
          ? new Date(b.start_date || 0) - new Date(a.start_date || 0)
          : 0
      );

    const columns = fields.map((f) => ({
      id: f.key,
      header: f.field,
      width: f.width,
      accessor: (row) => this.readPath(row, f.key),
      ...(typeof f.formatter === 'function'
        ? { cell: (row) => f.formatter(this.readPath(row, f.key)) }
        : {}),
    }));

    return (
      <PrismDataGrid
        keyField="_rowId"
        caption={dataType}
        data={rows}
        columns={columns}
        emptyMessage="No Entry"
      />
    );
  };

  renderWarning = () => {
    if (this.state.submission.submitted_at === null) {
      return (
        <PrismBanner tone="danger" layout="inline">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          This application has not been completed yet!
        </PrismBanner>
      );
    }
    if (this.state.submission.ApplicationStatus.status === 'Withdrawn') {
      return (
        <PrismBanner tone="danger" layout="inline">
          <FontAwesomeIcon icon={faExclamationTriangle} />
          This application has been withdrawn!
        </PrismBanner>
      );
    }
    return;
  };

  renderManagerApproval = () => {
    const { ManagerApproval } = this.state.submission;
    if (this.state.submission.ApplicantType.applicant_type !== 'mentee') return null;

    if (!ManagerApproval) {
      return (
        <div style={{ padding: '0.75rem 1rem', borderRadius: '6px', backgroundColor: 'var(--color-surface-alt)', border: '1px solid var(--color-border)' }}>
          <span style={{ color: 'var(--color-text-faded)', fontStyle: 'italic' }}>Pending manager review</span>
        </div>
      );
    }

    const { approved, comments, approved_at, is_override, OverrideUser } = ManagerApproval;
    const statusColor = approved ? 'var(--color-success)' : 'var(--color-danger)';
    const statusText = approved ? 'Approved' : 'Rejected';
    const formattedDate = approved_at
      ? new Date(approved_at).toLocaleDateString('en-US', { year: 'numeric', month: 'long', day: 'numeric', hour: 'numeric', minute: '2-digit' })
      : null;

    // Attribution: override shows the override user's name; normal shows the manager email.
    // Under a blind manager mask every name in this card is hidden, including History actors.
    const hideNames = !!this.props.blindMask?.manager;
    const authorName = hideNames
      ? null
      : is_override && OverrideUser
        ? OverrideUser.name
        : this.state.submission.manager;
    const authorLabel = is_override ? 'Override by' : 'Manager';

    const toggleHistory = () => {
      if (!this.state.approvalHistoryOpen && this.state.approvalHistory.length === 0) {
        axios.get(`/api/applications/${this.state.submission.id}/approve/history`)
          .then((res) => this.setState({ approvalHistory: res.data, approvalHistoryOpen: true }))
          .catch(() => this.setState({ approvalHistoryOpen: true }));
      } else {
        this.setState({ approvalHistoryOpen: !this.state.approvalHistoryOpen });
      }
    };

    return (
      <div style={{ padding: '0.75rem 1rem', borderRadius: '6px', border: `1px solid ${statusColor}`, backgroundColor: 'var(--color-surface-alt)' }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: comments ? '0.5rem' : 0 }}>
          <span style={{
            display: 'inline-block', padding: '0.2rem 0.6rem', borderRadius: '999px',
            backgroundColor: statusColor, color: '#fff', fontSize: '0.8rem', fontWeight: 600,
          }}>
            {statusText}
          </span>
          {authorName && (
            <span style={{ fontSize: '0.85rem', color: 'var(--color-text)' }}>
              {authorLabel}: <strong>{authorName}</strong>
            </span>
          )}
          {formattedDate && (
            <span style={{ fontSize: '0.85rem', color: 'var(--color-text-faded)' }}>&mdash; {formattedDate}</span>
          )}
        </div>
        {comments && (
          <div style={{ fontStyle: 'italic', color: 'var(--color-text)', lineHeight: 1.5 }}>
            &ldquo;{comments}&rdquo;
          </div>
        )}
        <div
          onClick={toggleHistory}
          style={{ display: 'flex', alignItems: 'center', gap: '0.4rem', marginTop: '0.75rem', cursor: 'pointer', fontSize: '0.85rem', color: 'var(--color-primary)', userSelect: 'none' }}
        >
          <FontAwesomeIcon
            icon={faChevronDown}
            style={{ transition: 'transform 0.2s', transform: this.state.approvalHistoryOpen ? 'rotate(180deg)' : 'rotate(0)' }}
          />
          <span>History</span>
        </div>
        {this.state.approvalHistoryOpen && (
          <div style={{ marginTop: '0.5rem', borderTop: '1px solid var(--color-border)', paddingTop: '0.5rem' }}>
            {this.state.approvalHistory.length === 0 ? (
              <span style={{ fontSize: '0.85rem', color: 'var(--color-text-faded)', fontStyle: 'italic' }}>No history available</span>
            ) : (
              <ul style={{ listStyle: 'none', padding: 0, margin: 0 }}>
                {this.state.approvalHistory.map((log, idx) => {
                  const ts = new Date(log.createdTimestamp).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
                  });
                  const actor = hideNames ? 'Hidden for blind review' : log.sourceUser?.name || 'System';
                  const prev = log.previousValue;
                  const next = log.newValue;
                  let action = next === 'true' ? 'Approved' : next === 'false' ? 'Rejected' : `Changed to "${next}"`;
                  if (prev !== null && prev !== undefined && prev !== '') {
                    const prevLabel = prev === 'true' ? 'Approved' : prev === 'false' ? 'Rejected' : `"${prev}"`;
                    action += ` (was ${prevLabel})`;
                  }

                  return (
                    <li key={idx} style={{ fontSize: '0.82rem', color: 'var(--color-text)', padding: '0.25rem 0', display: 'flex', gap: '0.5rem' }}>
                      <span style={{ color: 'var(--color-text-faded)', whiteSpace: 'nowrap' }}>{ts}</span>
                      <span><strong>{actor}</strong> &mdash; {action}</span>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        )}
      </div>
    );
  };

  renderPastApplications = () => {
    if (this.hasMultipleApplications()) {
      let pastApps = this.state.submission.Applications;

      // Sort by year in descending order (newest first)
      const sortedPastApps = [...pastApps].sort((a, b) => b.year - a.year);

      // Under blind review the old application would open unmasked, so show the year only.
      const blind = !!this.props.blindMask;
      const links = sortedPastApps
        .filter((a) => a.id !== this.state.submission.id && a.status_id === 2)
        .map((a) => blind ? (
          <span
            key={a.id}
            data-testid="past-application-chip"
            title="Hidden during blind review"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              padding: '0.4rem 0.85rem',
              borderRadius: '6px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-text)',
              fontSize: '0.9rem',
            }}
          >
            {a.year} Application
          </span>
        ) : (
          <a
            key={a.id}
            href={'/application/' + a.id}
            target="_blank"
            rel="noopener noreferrer"
            style={{
              display: 'inline-flex',
              alignItems: 'center',
              gap: '0.4rem',
              padding: '0.4rem 0.85rem',
              borderRadius: '6px',
              border: '1px solid var(--color-border)',
              backgroundColor: 'var(--color-surface)',
              color: 'var(--color-primary)',
              fontSize: '0.9rem',
              textDecoration: 'none',
              transition: 'background-color 0.15s',
            }}
          >
            <FontAwesomeIcon icon={faExternalLinkAlt} style={{ fontSize: '0.75rem' }} />
            {a.year} Application
          </a>
        ));

      if (links.length === 0) return null;

      return (
        <div className="app-section" style={{ marginBottom: '1.25rem' }}>
          <h2 className="app-section-heading">
            <FontAwesomeIcon icon={faInfoCircle} /> Previous Applications
          </h2>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '0.5rem' }}>
            {links}
          </div>
        </div>
      );
    }
  };

  safeParseHTML = (htmlString) => {
    if (!htmlString || typeof htmlString !== 'string') {
      return htmlString || '';
    }
    // Replace non-breaking spaces with regular spaces to allow proper word wrapping
    const processedString = htmlString.replace(/&nbsp;/g, ' ');
    // Defense-in-depth: sanitize stored HTML before rendering, even though it is
    // sanitized on write. Protects against any legacy/unsanitized rows.
    const safeString = DOMPurify.sanitize(processedString);
    return parse(safeString);
  };

  render() {
    if (this.state.loading) {
      return <Loader />;
    }

    if (this.state.loadError) {
      return (
        <PrismBanner tone="info" variant="notification">
          {this.state.loadError}
        </PrismBanner>
      );
    }

    if (
      this.props.currentUser.Role.permission_level <
        this.state.validPermissionLevel &&
      this.props.currentUser.User.id !== this.state.submission.user_id
    ) {
      return <Unauthorized />;
    }
    const isPending =
      this.state.submission.status_id === APPLICATION_STATUS_ID_PENDING;
    const isCommitteeLevelOrHigher =
      this.props.currentUser.Role.permission_level >=
      this.state.validPermissionLevel;
    const showWarnings = !isPending && isCommitteeLevelOrHigher;
    // Blind review omits masked fields from the UI only; the API response still carries them.
    const mask = this.props.blindMask || {};

    return (
      <>
        {this.props.hideHeader ? null : (
          <PageHeader title="CLEAR Application" />
        )}

        <div
          className={'application' + (this.props.hideHeader ? ' embedded' : '')}
          style={{ maxWidth: '1140px', margin: '0 auto', padding: '0 15px' }}
        >
          <div style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'flex-start', gap: '1.5rem' }}>
            <div style={{ flex: '0 0 33.333%', minWidth: '280px' }} className="app-details">
              <Card
                image_src="/images/headshots/default.svg"
                image_variant="small"
                title={mask.name ? this.props.blindLabel || 'Applicant' : this.state.submission.User.name}
                subtitles={[
                  mask.email ? null : (
                    // eslint-disable-next-line react/jsx-key
                    <a href={`mailto:${this.state.submission.User.email}`}>
                      {this.state.submission.User.email}
                    </a>
                  ),
                  mask.title ? null : this.state.submission.title,
                ].filter((s) => s !== null)}
              >
                <div>
                  <dl>
                    <dt>Applicant Type</dt>
                    <dd style={{ textTransform: 'capitalize' }}>
                      {this.state.submission.ApplicantType.applicant_type}
                    </dd>

                    <dt>Chapter</dt>
                    <dd>{this.state.submission.Chapter.location}</dd>

                    {!mask.department && (
                      <>
                        <dt>Department</dt>
                        <dd>{this.state.submission.department}</dd>
                      </>
                    )}

                    {!mask.manager && (
                      <>
                        <dt>Manager</dt>
                        <dd>{this.state.submission.manager}</dd>
                      </>
                    )}

                    {!mask.hireDate && (
                    <>
                    <dt>
                      {this.isTenuredEnough() ? (
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            color="var(--color-success)"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            color="var(--color-danger)"
                            title="Does not meet tenure requirements!"
                          />
                        )}
                      {' '}
                      {isCommitteeLevelOrHigher
                        ? 'Tenure'
                        : this.state.submission.hire_date
                          ? 'Hire Date'
                          : 'Tenure'}
                    </dt>
                    <dd>
                      {isCommitteeLevelOrHigher ? (
                        <>
                          Years at Company: {this.state.submission.years_with_company}
                          <br />
                          Desired Tenure: {(this.state.requiredTenureMonths / 12).toFixed(2)}
                        </>
                      ) : this.state.submission.hire_date ? (
                        formatMonthYear(this.state.submission.hire_date)
                      ) : (
                        `Years at Company: ${this.state.submission.years_with_company}`
                      )}
                    </dd>
                    </>
                    )}
                    {this.state.submission.erg?.split(',').map(s => s.trim()).filter(Boolean).length > 0 && (
                      <>
                        <dt>ERG Details</dt>
                        <dd>{this.state.submission.erg.split(',').map(s => s.trim()).filter(Boolean).sort((a, b) => a.localeCompare(b)).join(', ')}</dd>
                      </>
                    )}

                    {!mask.location && (
                      <>
                        <dt>Azure Office Location</dt>
                        <dd>{this.state.submission.azure_office_location || this.state.submission.User?.office_location || ''}</dd>
                      </>
                    )}

                    {!mask.location && this.state.submission.location_comments?.trim() && (
                      <>
                        <dt>Declared Office Location</dt>
                        <dd>{this.state.submission.location_comments}</dd>
                      </>
                    )}
                  </dl>
                </div>
              </Card>
            </div>
            <div style={{ flex: '1 1 0', minWidth: '300px' }} className="app-content">
              {showWarnings && (
                <>
                  {this.renderWarning()}
                  {this.renderPastApplications()}
                  {this.state.submission.ApplicantType.applicant_type ===
                    'mentee' && (
                    <div className="app-section">
                      <h2 className="app-section-heading">
                        {this.isManagerApproved() ? (
                          <FontAwesomeIcon
                            icon={faCheckCircle}
                            color="var(--color-success)"
                          />
                        ) : (
                          <FontAwesomeIcon
                            icon={faExclamationTriangle}
                            color="var(--color-danger)"
                            title="Manager has not yet approved this application!"
                          />
                        )}
                        Manager Approval
                      </h2>
                      {this.renderManagerApproval()}
                    </div>
                  )}
                </>
              )}
              <div className="app-section">
                <h2 className="app-section-heading">Education</h2>
                {this.buildTable('EducationEntries')}
              </div>

              <div className="app-section">
                <h2 className="app-section-heading">Career</h2>
                {this.buildTable('CareerEntries')}
              </div>

              {this.state.submission.relationship_count > 0 && (
                <div className="app-section">
                  <h2 className="app-section-heading">Relationships</h2>
                  <b>
                    Number of relationships:{' '}
                    {this.state.submission.relationship_count}
                  </b>
                </div>
              )}

              {this.state.submission.has_mentoring_experience !== null &&
                this.state.submission.has_mentoring_experience !== undefined && (
                <div className="app-section">
                  <h2 className="app-section-heading">Mentoring Experience</h2>
                  <dl>
                    <dt>Has Professional Mentoring Experience</dt>
                    <dd>
                      {this.state.submission.has_mentoring_experience
                        ? 'Yes'
                        : 'No'}
                    </dd>

                    {this.state.submission.has_mentoring_experience && (
                      <>
                        <dt>Total Years of Mentoring</dt>
                        <dd>
                          {this.state.submission.years_of_mentoring
                            ? `${this.state.submission.years_of_mentoring} years`
                            : 'Not specified'}
                        </dd>

                        <dt>Lifetime Total Number of Mentees</dt>
                        <dd>
                          {this.state.submission.number_of_mentees
                            ? `${this.state.submission.number_of_mentees} mentees`
                            : 'Not specified'}
                        </dd>

                        {this.state.submission.mentoring_self_assessment && (
                          <>
                            <dt>Self Assessment</dt>
                            <dd>
                              <div className="mentoring-self-assessment">
                                {this.safeParseHTML(
                                  this.state.submission
                                    .mentoring_self_assessment
                                )}
                              </div>
                            </dd>
                          </>
                        )}
                      </>
                    )}
                  </dl>
                </div>
              )}

              <div className="app-section">
                <h2 className="app-section-heading">Skills</h2>
                <p className="app-section-prompt">{this.getCompInfo()}</p>
                {this.buildTable('CompetencyRatings')}
              </div>

              <div className="app-section">
                <h2 className="app-section-heading">Additional Skills</h2>
                <p className="app-section-prompt">{this.getCompAdditionalSkillsInfo()}</p>
                <div className="additional_skills">
                  {this.state.submission.additional_skills?.trim()
                    ? this.safeParseHTML(this.state.submission.additional_skills)
                    : ' - No Entry'}
                </div>
              </div>

              <div className="app-section">
                <h2 className="app-section-heading">Essay</h2>
                <p className="app-section-prompt">{this.getEssayInfo()}</p>
                <div className="essay">
                  {this.safeParseHTML(this.state.submission.essay)}
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  }
}

export default SubmittedApp;